import { Component, OnInit, Input, OnDestroy, OnChanges } from "@angular/core";
import { Router } from "@angular/router";
import { AngularFireDatabase } from "@angular/fire/database";
import { Subject } from "rxjs";
import { MatDialog } from "@angular/material/dialog";

import { AngularFirestore } from "@angular/fire/firestore";
import { languages as allLanguages } from "src/app/Models/language-models.interface";
import { xorBy } from "lodash";
import { NewLanguageDialogComponent } from "../admin-new-language-dialog/admin-new-language-dialog.component";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import {
  DraftStatus,
  LanguageDraft,
} from "src/app/Models/language-models.interface";
import { DefaultTranslation } from "src/app/locale/default-translation";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { AdminLanguagesBottomSheetComponent } from "src/app/admin-languages-bottom-sheet/admin-languages-bottom-sheet.component";

dayjs.extend(utc);

@Component({
  selector: "app-admin-languages",
  templateUrl: "./admin-languages.component.html",
  styleUrls: ["./admin-languages.component.css"],
})
export class AdminLanguagesComponent implements OnInit, OnDestroy {
  @Input() superAdmin: boolean;
  leaving = new Subject();
  constructor(
    public dialog: MatDialog,
    private afs: AngularFirestore,
    private router: Router,
    private _bottomSheet: MatBottomSheet
  ) {}

  ngOnInit(): void {
    this.setup();
  }
  languages: LanguageDraft[];
  allLanguages: LanguageDraft[];
  columns: string[] = ["code", "name", "nativeName", "actions"];
  setup = () => {
    this.afs
      .collection<LanguageDraft>("Languages")
      .valueChanges()
      .takeUntil(this.leaving)
      .subscribe((languages) => {
        this.languages = languages;
        this.allLanguages = xorBy(
          Object.keys(allLanguages).map((key) => allLanguages[key]),
          languages,
          "code"
        ).sort((a, b) => a.code.localeCompare(b.code));
      });
  };

  displayActions = (langConfig: LanguageDraft) => {
    if (langConfig.code == "en") {
      alert("No Actions are available for Enlish at this time");
      return;
    }
    this.afs
      .collection("Languages")
      .doc(langConfig.code)
      .collection("Drafts", (ref) => ref.orderBy("version", "desc").limit(1))
      .get()
      .take(1)
      .subscribe((data) => {
        this._bottomSheet.open(AdminLanguagesBottomSheetComponent, {
          data: {
            language: (data.docs?.[0]?.data?.() as LanguageDraft) ?? {},
          },
        });
      });
  };

  openDialog(): void {
    const dialogRef = this.dialog.open(NewLanguageDialogComponent, {
      width: "400px",
      data: { language: {} },
    });

    dialogRef.afterClosed().subscribe((result: LanguageDraft) => {
      // create a draft
      if (result && result.code) {
        this.createLanguage(result);
      }
    });
  }

  createLanguage = async (language: LanguageDraft) => {
    // create Languages/{code} for meta
    // create Languages/{code}/Config copy english here
    // create Languages/{code}/Lessons copy english here
    // later on we can create /Languages/{code}/Editors/uid approved true
    this.createMetaIfNeeded(language);
    this.createDraftIfNeeded(language);
  };

  createMetaIfNeeded = (language: LanguageDraft) => {
    this.afs
      .collection("Languages")
      .doc(language.code)
      .valueChanges()
      .take(1)
      .subscribe((lang) => {
        if (!lang) {
          this.createLanguageMeta(language);
        }
      });
  };

  createLanguageMeta = (language: LanguageDraft) => {
    this.afs
      .collection("Languages")
      .doc(language.code)
      .set({
        ...language,
        createdAt: dayjs.utc().toISOString(),
      });
  };

  createDraftIfNeeded = (language: LanguageDraft) => {
    this.checkIfActiveDraftExists(language);
  };

  // Make site-config work off of draft
  // pull code from url and get draft

  checkIfActiveDraftExists = (language: LanguageDraft) => {
    this.afs
      .collection("Languages")
      .doc(language.code)
      .collection("Drafts")
      .valueChanges()
      .take(1)
      .subscribe((drafts) => {
        const activeDrafts = drafts.filter(
          (draft) => draft.status === DraftStatus.EDITING
        );

        if (activeDrafts.length > 0) {
          ``;
        } else {
          this.createDraft(language, drafts.length + 1);
        }
      });
  };

  createDraft = (language: LanguageDraft, version: number) => {
    this.afs
      .collection("Languages")
      .doc(language.code)
      .collection("Drafts")
      .doc<LanguageDraft>(`${version}`)
      .set({
        ...language,
        version,
        status: DraftStatus.EDITING,
        createdAt: dayjs.utc().toISOString(),
      });
    this.createDraftConfig(language, version);
    this.createDraftLessons(language, version);
  };

  createDraftConfig = (language: LanguageDraft, version: number) => {
    Object.keys(DefaultTranslation).forEach((key) => {
      this.afs
        .collection("Languages")
        .doc(language.code)
        .collection("Drafts")
        .doc(`${version}`)
        .collection("Config")
        .doc(key)
        .set(DefaultTranslation[key]);
    });
  };

  createDraftLessons = (language: LanguageDraft, version: number) => {
    this.afs
      .collection("Languages")
      .doc("en")
      .collection("Lessons")
      .valueChanges()
      .take(1)
      .subscribe((lessons) => {
        lessons.forEach((lesson, index) => {
          this.afs
            .collection("Languages")
            .doc(language.code)
            .collection("Drafts")
            .doc(`${version}`)
            .collection("Lessons")
            .doc(String(index + 1))
            .set(lesson);
        });
      });
  };

  publishDraft = (language: LanguageDraft, version: number) => {
    // copy the draft and set-override Config and Lessons sections
    // update updatedAt on main section
    // mark draft as Published
    this.publishConfig(language, version);
    this.publishLessons(language, version);
  };

  publishConfig = (language: LanguageDraft, version: number) => {
    Object.keys(DefaultTranslation).forEach((key) => {
      this.afs
        .collection("Languages")
        .doc(language.code)
        .collection("Drafts")
        .doc(`${version}`)
        .collection("Config")
        .doc(key)
        .valueChanges()
        .take(1)
        .subscribe((configProperty) => {
          this.afs
            .collection("Languages")
            .doc(language.code)
            .collection("Config")
            .doc(key)
            .set(configProperty);
        });
    });
  };

  publishLessons = (language: LanguageDraft, version: number) => {
    this.afs
      .collection("Languages")
      .doc(language.code)
      .collection("Drafts")
      .doc(`${version}`)
      .collection("Lessons")
      .valueChanges()
      .take(1)
      .subscribe((lessons) => {
        lessons.forEach((lesson, index) => {
          this.afs
            .collection("Languages")
            .doc(language.code)
            .collection("Lessons")
            .doc(String(index + 1))
            .set(lesson);
        });
      });
  };

  updateDraftToPublished = (language: LanguageDraft, version: number) => {
    this.afs
      .collection("Languages")
      .doc(language.code)
      .collection("Drafts")
      .doc<LanguageDraft>(`${version}`)
      .update({
        status: DraftStatus.APPROVED,
        publishedAt: dayjs.utc().toISOString(),
      });
  };

  rejectDraft = (language: LanguageDraft, version: number) => {
    this.updateDraftToRejected(language, version);
  };

  updateDraftToRejected = (language: LanguageDraft, version: number) => {
    this.afs
      .collection("Languages")
      .doc(language.code)
      .collection("Drafts")
      .doc(`${version}`)
      .update({
        status: DraftStatus.REJECTED,
        rejectedAt: dayjs.utc().toISOString(),
      });
  };

  process = (data) => {
    const keys = Object.keys(data);
    // keys.forEach((key) => {
    //   this.afs
    //     .collection("Languages")
    //     .doc("en")
    //     .collection("Config")
    //     .doc(key)
    //     .set(data[key]);
    // });
  };

  ngOnDestroy(): void {
    this.leaving.next(null);
  }
}
